import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

// Shown instead of a CRM page to anyone without CRM access.
export const NoCrmAccess = () => (
  <div style={{ maxWidth: '760px' }}>
    <div className="page-header">
      <div>
        <h1 className="page-title">Customers</h1>
      </div>
    </div>
    <div className="card">
      <div className="empty-state">
        <p>You do not have access to the CRM. Ask an admin to switch it on.</p>
      </div>
    </div>
  </div>
);

export const STATUS_LABELS = { lead: 'Lead', customer: 'Customer', inactive: 'Inactive' };
export const STATUS_BADGE = { lead: 'badge-pending', customer: 'badge-approved', inactive: 'badge-cancelled' };
export const KIND_LABELS = { business: 'Business (B2B)', individual: 'Individual (B2C)' };

export const blankCustomer = {
  kind: 'business', name: '', account_number: '', status: 'customer', email: '', phone: '', website: '',
  address_line1: '', address_line2: '', town: '', county: '', postcode: '', source: '', tags: '', notes: '', owner_id: '',
};

export const formatDate = (value) => {
  if (!value) return '-';
  try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return '-'; }
};

// Download a CSV from the API with the login token attached.
const download = async (path, fallbackName) => {
  const res = await api.get(path, { responseType: 'blob' });
  let filename = fallbackName;
  const disp = res.headers['content-disposition'];
  const match = disp && disp.match(/filename="?([^"]+)"?/);
  if (match) filename = match[1];
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// The customer form fields, shared by "New customer" here and "Edit" on the customer page.
export const CustomerFields = ({ form, onChange, staff }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0 16px' }}>
    <div className="form-group">
      <label className="form-label">Type</label>
      <select name="kind" className="form-control" value={form.kind} onChange={onChange}>
        {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </div>
    <div className="form-group">
      <label className="form-label">Status</label>
      <select name="status" className="form-control" value={form.status} onChange={onChange}>
        {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </div>
    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
      <label className="form-label">{form.kind === 'business' ? 'Business name' : 'Full name'}</label>
      <input name="name" className="form-control" value={form.name} onChange={onChange} autoComplete="off" />
    </div>
    <div className="form-group">
      <label className="form-label">Account number (optional)</label>
      <input name="account_number" className="form-control" value={form.account_number} onChange={onChange} autoComplete="off" />
    </div>
    <div className="form-group">
      <label className="form-label">Account owner</label>
      <select name="owner_id" className="form-control" value={form.owner_id} onChange={onChange}>
        <option value="">Nobody yet</option>
        {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </div>
    <div className="form-group">
      <label className="form-label">Main email</label>
      <input name="email" type="email" className="form-control" value={form.email} onChange={onChange} autoComplete="off" />
    </div>
    <div className="form-group">
      <label className="form-label">Main phone</label>
      <input name="phone" type="tel" className="form-control" value={form.phone} onChange={onChange} autoComplete="off" />
    </div>
    {form.kind === 'business' && (
      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
        <label className="form-label">Website</label>
        <input name="website" className="form-control" value={form.website} onChange={onChange} autoComplete="off" />
      </div>
    )}
    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
      <label className="form-label">Address</label>
      <input name="address_line1" className="form-control" value={form.address_line1} onChange={onChange} autoComplete="off" placeholder="Line 1" style={{ marginBottom: '8px' }} />
      <input name="address_line2" className="form-control" value={form.address_line2} onChange={onChange} autoComplete="off" placeholder="Line 2" />
    </div>
    <div className="form-group">
      <label className="form-label">Town</label>
      <input name="town" className="form-control" value={form.town} onChange={onChange} autoComplete="off" />
    </div>
    <div className="form-group">
      <label className="form-label">County</label>
      <input name="county" className="form-control" value={form.county} onChange={onChange} autoComplete="off" />
    </div>
    <div className="form-group">
      <label className="form-label">Postcode</label>
      <input name="postcode" className="form-control" value={form.postcode} onChange={onChange} autoComplete="off" />
    </div>
    <div className="form-group">
      <label className="form-label">Where they came from</label>
      <input name="source" className="form-control" value={form.source} onChange={onChange} autoComplete="off" placeholder="e.g. website, trade counter, referral" />
    </div>
    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
      <label className="form-label">Tags (comma separated)</label>
      <input name="tags" className="form-control" value={form.tags} onChange={onChange} autoComplete="off" placeholder="e.g. fleet, workshop, trailer parts" />
      <p style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '4px' }}>Tags are how you build email segments. They go out in the export.</p>
    </div>
    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
      <label className="form-label">Notes</label>
      <textarea name="notes" className="form-control" rows={3} value={form.notes} onChange={onChange} />
    </div>
  </div>
);

export const customerPayload = (form) => ({
  ...form,
  tags: String(form.tags || '').split(',').map(t => t.trim()).filter(Boolean),
  owner_id: form.owner_id ? Number(form.owner_id) : null,
});

const CrmCustomers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: '', kind: '', status: '', tag: '', owner: '', archived: false });
  const [tags, setTags] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [noAccess, setNoAccess] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blankCustomer);
  const [contact, setContact] = useState({ first_name: '', last_name: '', job_title: '', email: '', mobile: '' });
  const [saving, setSaving] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importTag, setImportTag] = useState('');
  const [importSource, setImportSource] = useState('HubSpot export');
  const [importResult, setImportResult] = useState(null);
  // 'contacts' (HubSpot or this page's export) or 'erp' (the ERP accounts export, admins only)
  const [importKind, setImportKind] = useState('contacts');
  // ERP import only: consent everyone in the file already gave on the old platform.
  const [erpConsent, setErpConsent] = useState({ email: false, sms: false, source: '' });
  const [exportOpts, setExportOpts] = useState({ consent: 'email', kind: '', tag: '' });

  // The server re-checks access on every request; if it has just been switched
  // off, show the plain message rather than an error.
  const denied = (err) => {
    if (err.response?.status !== 403) return false;
    setNoAccess(true);
    return true;
  };

  const load = useCallback(async (f, p) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(f)) if (v) params.set(k, String(v));
      params.set('page', String(p));
      const res = await api.get(`/api/crm/customers?${params}`);
      setRows(res.data.rows);
      setTotal(res.data.total);
    } catch (err) {
      if (!denied(err)) setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get('/api/crm/staff').then(r => setStaff(r.data)).catch(() => {});
    api.get('/api/crm/tags').then(r => setTags(r.data)).catch(() => {});
  }, []);

  // Search as you type, with a short pause so every key press is not a request.
  useEffect(() => {
    const t = setTimeout(() => load(filters, page), 250);
    return () => clearTimeout(t);
  }, [filters, page, load]);

  const setFilter = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setPage(1);
  };

  const openCreate = () => {
    setForm({ ...blankCustomer, owner_id: String(user?.id || '') });
    setContact({ first_name: '', last_name: '', job_title: '', email: '', mobile: '' });
    setError('');
    setModal('create');
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return setError('Please enter a name');
    setSaving(true);
    setError('');
    try {
      const body = customerPayload(form);
      const hasContact = Object.values(contact).some(v => v.trim());
      if (form.kind === 'business' && hasContact) body.contact = contact;
      const res = await api.post('/api/crm/customers', body);
      setModal(null);
      navigate(`/crm/customers/${res.data.id}`);
    } catch (err) {
      if (!denied(err)) setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!importFile) return setError('Choose a CSV file first');
    setSaving(true);
    setError('');
    try {
      const data = new FormData();
      data.append('file', importFile);
      data.append('tag', importTag);
      data.append('source', importSource);
      if (importKind === 'erp') {
        data.append('consent_email', String(erpConsent.email));
        data.append('consent_sms', String(erpConsent.sms));
        data.append('consent_source', erpConsent.source);
      }
      const path = importKind === 'erp' ? '/api/crm/import-erp' : '/api/crm/import';
      const res = await api.post(`${path}${dryRun ? '?dry_run=true' : ''}`, data);
      setImportResult(res.data);
      if (!dryRun) {
        setSuccess(importKind === 'erp'
          ? `ERP accounts imported: ${res.data.customers_created} new, ${res.data.customers_updated} updated, ${res.data.contacts_created} new contacts${res.data.consent_email_set || res.data.consent_sms_set ? `; opted in: ${res.data.consent_email_set} email, ${res.data.consent_sms_set} SMS` : ''}`
          : `Imported: ${res.data.customers_created} new customers, ${res.data.contacts_created} new contacts, ${res.data.contacts_updated} updated`);
        load(filters, page);
        api.get('/api/crm/tags').then(r => setTags(r.data)).catch(() => {});
      }
    } catch (err) {
      if (!denied(err)) setError(err.response?.data?.error || 'Import failed');
    } finally {
      setSaving(false);
    }
  };

  const runExport = async () => {
    setError('');
    try {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(exportOpts)) if (v) params.set(k, v);
      await download(`/api/crm/export?${params}`, 'tranzparts-crm.csv');
      setModal(null);
    } catch (err) {
      if (!denied(err)) setError('Export failed');
    }
  };

  const formChange = (e) => { const { name, value } = e.target; setForm(prev => ({ ...prev, [name]: value })); setError(''); };
  const contactChange = (e) => { const { name, value } = e.target; setContact(prev => ({ ...prev, [name]: value })); setError(''); };

  if (noAccess) return <NoCrmAccess />;

  const pages = Math.max(1, Math.ceil(total / 50));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">Business and retail customers, their contacts and marketing consent</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => { setImportFile(null); setImportResult(null); setImportTag(''); setImportKind('contacts'); setErpConsent({ email: false, sms: false, source: '' }); setError(''); setModal('import'); }}>Import</button>
          <button className="btn btn-outline" onClick={() => { setError(''); setModal('export'); }}>Export for Klaviyo</button>
          <button className="btn btn-primary" onClick={openCreate}>+ New customer</button>
        </div>
      </div>

      {error && !modal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', alignItems: 'end' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <input name="q" className="form-control" value={filters.q} onChange={setFilter} placeholder="Search name, account, email, phone, postcode or contact" autoComplete="off" />
          </div>
          <select name="kind" className="form-control" value={filters.kind} onChange={setFilter}>
            <option value="">All types</option>
            <option value="business">Business (B2B)</option>
            <option value="individual">Individual (B2C)</option>
          </select>
          <select name="status" className="form-control" value={filters.status} onChange={setFilter}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select name="tag" className="form-control" value={filters.tag} onChange={setFilter}>
            <option value="">All tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select name="owner" className="form-control" value={filters.owner} onChange={setFilter}>
            <option value="">Any owner</option>
            <option value="me">Mine</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
            <input type="checkbox" name="archived" checked={filters.archived} onChange={setFilter} style={{ accentColor: 'var(--red)' }} />
            Show archived
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{total.toLocaleString('en-GB')} {filters.archived ? 'archived ' : ''}customer{total === 1 ? '' : 's'}</h3>
        </div>
        {loading ? (
          <div className="loader">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <h3>No customers found</h3>
            <p>Add one, or import your HubSpot export</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Town</th>
                  <th>Contacts</th>
                  <th>Owner</th>
                  <th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(c => (
                  <tr key={c.id} onClick={() => navigate(`/crm/customers/${c.id}`)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 500 }}>
                      {c.name}
                      {c.account_number && <div style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{c.account_number}</div>}
                      {c.tags?.length > 0 && <div style={{ fontSize: '11px', color: 'var(--grey-mid)' }}>{c.tags.join(', ')}</div>}
                    </td>
                    <td>{c.kind === 'business' ? 'B2B' : 'B2C'}</td>
                    <td><span className={`badge ${STATUS_BADGE[c.status]}`}>{STATUS_LABELS[c.status]}</span></td>
                    <td style={{ color: 'var(--grey-mid)' }}>{[c.town, c.postcode].filter(Boolean).join(', ') || '-'}</td>
                    <td>{c.contact_count}</td>
                    <td style={{ color: 'var(--grey-mid)' }}>{c.owner_name || '-'}</td>
                    <td style={{ color: 'var(--grey-mid)' }}>{formatDate(c.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="card-body" style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span style={{ fontSize: '13px', color: 'var(--grey-mid)' }}>Page {page} of {pages}</span>
            <button className="btn btn-outline btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {modal === 'create' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" style={{ maxWidth: '640px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">New customer</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <CustomerFields form={form} onChange={formChange} staff={staff} />
              {form.kind === 'business' && (
                <>
                  <h4 style={{ margin: '8px 0 4px', fontSize: '14px' }}>Main contact (optional)</h4>
                  <p style={{ fontSize: '12px', color: 'var(--grey-mid)', margin: '0 0 12px' }}>The person you deal with. Their own email and mobile are what Klaviyo uses, so put them here rather than only in the business's main email above.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
                    <div className="form-group"><label className="form-label">First name</label><input name="first_name" className="form-control" value={contact.first_name} onChange={contactChange} autoComplete="off" /></div>
                    <div className="form-group"><label className="form-label">Last name</label><input name="last_name" className="form-control" value={contact.last_name} onChange={contactChange} autoComplete="off" /></div>
                    <div className="form-group"><label className="form-label">Job title</label><input name="job_title" className="form-control" value={contact.job_title} onChange={contactChange} autoComplete="off" /></div>
                    <div className="form-group"><label className="form-label">Contact's email</label><input name="email" type="email" className="form-control" value={contact.email} onChange={contactChange} autoComplete="off" /></div>
                    <div className="form-group"><label className="form-label">Contact's mobile</label><input name="mobile" type="tel" className="form-control" value={contact.mobile} onChange={contactChange} autoComplete="off" /></div>
                  </div>
                </>
              )}
              <p style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>Marketing consent starts as unknown. Set it on the customer page once they have said yes.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving...' : 'Save customer'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'import' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-scroll" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Import customers</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              {user?.role === 'admin' && (
                <div className="form-group">
                  <label className="form-label">What are you importing?</label>
                  <select className="form-control" value={importKind} onChange={e => { setImportKind(e.target.value); setImportResult(null); setError(''); }}>
                    <option value="contacts">Contacts CSV (HubSpot, or an export from this page)</option>
                    <option value="erp">ERP accounts export</option>
                  </select>
                </div>
              )}
              {importKind === 'erp' ? (
              <p style={{ fontSize: '13px', color: 'var(--grey-mid)', marginTop: 0, marginBottom: '16px' }}>
                The trade accounts export from the ERP. Run it again whenever you have a fresh export: accounts are matched on their ERP reference (then exact name), so they are updated, never duplicated.
                The ERP's account details overwrite what is here, but notes, owner, tags you added, consent and the timeline are never touched. Stopped accounts become inactive. Everyone comes in as "not asked" for marketing.
              </p>
              ) : (
              <p style={{ fontSize: '13px', color: 'var(--grey-mid)', marginTop: 0, marginBottom: '16px' }}>
                A CSV export from HubSpot (Contacts, Export, CSV) or from this page. Contacts are matched by email: anyone already here only has their blank fields filled in.
                Nobody is marked as opted in by an import unless the file has an "Email Consent" or "SMS Consent" column saying so. HubSpot's "Unsubscribed from all email" marks them opted out.
              </p>
              )}
              <div className="form-group">
                <label className="form-label">CSV file</label>
                <input type="file" accept=".csv,text/csv" className="form-control" onChange={e => { setImportFile(e.target.files[0] || null); setImportResult(null); }} />
              </div>
              {importKind === 'erp' && (
                <div className="form-group">
                  <label className="form-label">Already opted in (optional)</label>
                  <p style={{ fontSize: '12px', color: 'var(--grey-mid)', marginTop: 0 }}>Tick only what everyone in this file has actually agreed to. It fills "not asked" only; anyone opted out stays opted out.</p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={erpConsent.email} onChange={e => { setErpConsent(c => ({ ...c, email: e.target.checked })); setImportResult(null); }} style={{ accentColor: 'var(--red)' }} /> Email marketing
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={erpConsent.sms} onChange={e => { setErpConsent(c => ({ ...c, sms: e.target.checked })); setImportResult(null); }} style={{ accentColor: 'var(--red)' }} /> SMS marketing
                  </label>
                  {(erpConsent.email || erpConsent.sms) && (
                    <input className="form-control" value={erpConsent.source} onChange={e => { setErpConsent(c => ({ ...c, source: e.target.value })); setImportResult(null); }} placeholder="Where they opted in, e.g. opted in on HubSpot before the move" autoComplete="off" />
                  )}
                </div>
              )}
              {importKind !== 'erp' && (<>
              <div className="form-group">
                <label className="form-label">Tag everyone in this file (optional)</label>
                <input className="form-control" value={importTag} onChange={e => setImportTag(e.target.value)} placeholder="e.g. hubspot-import" autoComplete="off" />
              </div>
              <div className="form-group">
                <label className="form-label">Source</label>
                <input className="form-control" value={importSource} onChange={e => setImportSource(e.target.value)} autoComplete="off" />
              </div>
              </>)}
              {importResult && (
                <div className={`alert ${importResult.dry_run ? 'alert-info' : 'alert-success'}`}>
                  <strong>{importResult.dry_run ? 'Preview, nothing saved yet' : 'Imported'}</strong><br />
                  {importResult.rows} rows read. {(importResult.consent_email_set > 0 || importResult.consent_sms_set > 0) && `Opted in: ${importResult.consent_email_set} email, ${importResult.consent_sms_set} SMS. `}{importResult.customers_created} new customers,{importResult.customers_updated !== undefined && ` ${importResult.customers_updated} updated,`} {importResult.contacts_created} new contacts, {importResult.contacts_updated} existing contacts updated, {importResult.skipped} skipped.
                  {importResult.columns && <><br /><span style={{ fontSize: '12px' }}>Columns used: {importResult.columns.join(', ')}</span></>}
                  {importResult.problems.length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: '18px', fontSize: '12px' }}>
                      {importResult.problems.slice(0, 25).map((p, i) => <li key={i}>Line {p.line}: {p.error}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => runImport(true)} disabled={saving || !importFile}>Preview</button>
              <button className="btn btn-primary" onClick={() => runImport(false)} disabled={saving || !importResult?.dry_run}>{saving ? 'Working...' : 'Import'}</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'export' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Export for Klaviyo</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-group">
                <label className="form-label">Who</label>
                <select className="form-control" value={exportOpts.consent} onChange={e => setExportOpts(o => ({ ...o, consent: e.target.value }))}>
                  <option value="email">Contacts opted in to email</option>
                  <option value="sms">Contacts opted in to SMS (mobiles only)</option>
                  <option value="all">Everyone (not a marketing list)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" value={exportOpts.kind} onChange={e => setExportOpts(o => ({ ...o, kind: e.target.value }))}>
                  <option value="">B2B and B2C</option>
                  <option value="business">Business (B2B) only</option>
                  <option value="individual">Individual (B2C) only</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tag</label>
                <select className="form-control" value={exportOpts.tag} onChange={e => setExportOpts(o => ({ ...o, tag: e.target.value }))}>
                  <option value="">Any tag</option>
                  {tags.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>
                One row per contact, with Klaviyo's column names and phone numbers in +44 format. Archived customers are left out.
                "Everyone" includes people who have opted out, so never upload it to Klaviyo as a marketing list.
                Every export is recorded with your name.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={runExport}>Download CSV</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmCustomers;
